import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../stores';

/**
 * 注册页面
 */
const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [registrationType, setRegistrationType] = useState<'phone' | 'email' | 'wechat'>('phone');
  const [countryCode, setCountryCode] = useState('+86');
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    nickname: '',
    verificationCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const { register, sendVerificationCode, registerState, verificationState } = useStore();

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 注册成功后跳转
  useEffect(() => {
    if (registerState === 'success') {
      navigate('/chat');
    }
  }, [registerState, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // 验证密码
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setIsLoading(false);
      return;
    }

    // 验证密码强度
    if (formData.password.length < 6) {
      setError('密码至少需要6位字符');
      setIsLoading(false);
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(formData.password)) {
      setError('密码只能包含字母和数字');
      setIsLoading(false);
      return;
    }

    // 验证验证码
    if (!formData.verificationCode) {
      setError('请输入验证码');
      setIsLoading(false);
      return;
    }

    try {
      const registerData = {
        password: formData.password,
        nickname: formData.nickname,
        verificationCode: formData.verificationCode,
        ...(registrationType === 'phone' ? { phone: `${countryCode}${formData.phone}` } : { email: formData.email })
      };

      await register(registerData);
    } catch (err: any) {
      setError(err.message || '注册失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (countdown > 0) return;

    const contact = registrationType === 'phone' ? formData.phone : formData.email;
    if (!contact) {
      setError(`请输入${registrationType === 'phone' ? '手机号' : '邮箱'}`);
      return;
    }

    // 验证格式
    if (registrationType === 'phone') {
      if (countryCode === '+86' && !/^1[3-9]\d{9}$/.test(formData.phone)) {
        setError('请输入正确的手机号');
        return;
      } else if (countryCode !== '+86' && !/^\d{7,15}$/.test(formData.phone)) {
        setError('请输入正确的手机号');
        return;
      }
    }

    if (registrationType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('请输入正确的邮箱地址');
      return;
    }

    try {
      setError('');
      const codeData = {
        type: 'register' as const,
        ...(registrationType === 'phone' ? { phone: `${countryCode}${formData.phone}` } : { email: formData.email })
      };

      await sendVerificationCode(codeData);
      setCodeSent(true);
      setCountdown(60);
    } catch (err: any) {
      setError(err.message || '发送验证码失败');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* 标题 */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img
              src="/images/logo.jpg"
              alt="AI数理化助手"
              className="w-12 h-12 mr-3 rounded-lg shadow-lg object-cover"
            />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              AI数理化助手
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            注册账号开始学习
          </p>
        </div>

        {/* 注册表单 */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {codeSent && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                验证码已发送，请查收
              </div>
            )}



            <div>
              <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                用户名
              </label>
              <input
                id="nickname"
                name="nickname"
                type="text"
                required
                className="input"
                placeholder="请输入用户名"
                value={formData.nickname}
                onChange={handleChange}
              />
            </div>

            {/* 密码输入 - 只在非微信注册时显示 */}
            {registrationType !== 'wechat' && (
              <>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    密码
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="input"
                    placeholder="至少6位，字母或数字"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    确认密码
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="input"
                    placeholder="请再次输入密码"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            {/* 手机号、邮箱或微信输入 */}
            {registrationType === 'phone' ? (
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  手机号
                </label>
                <div className="flex space-x-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                  >
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+82">🇰🇷 +82</option>
                    <option value="+65">🇸🇬 +65</option>
                    <option value="+852">🇭🇰 +852</option>
                    <option value="+853">🇲🇴 +853</option>
                    <option value="+886">🇹🇼 +886</option>
                  </select>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    className="input flex-1"
                    placeholder={countryCode === '+86' ? '请输入手机号' : 'Enter phone number'}
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            ) : registrationType === 'email' ? (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="input"
                  placeholder="请输入邮箱地址"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  微信扫码注册
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                  <div className="w-32 h-32 mx-auto mb-4 bg-white rounded-lg flex items-center justify-center border-2 border-gray-300">
                    <div className="text-gray-400">
                      <span className="text-4xl">📱</span>
                      <p className="text-sm mt-2">二维码加载中...</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    使用微信扫描二维码完成注册
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    功能开发中，敬请期待
                  </p>
                </div>
              </div>
            )}

            {/* 验证码输入 - 只在非微信注册时显示 */}
            {registrationType !== 'wechat' && (
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  验证码
                </label>
                <div className="flex space-x-2">
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    required
                    className="input flex-1"
                    placeholder="请输入验证码"
                    value={formData.verificationCode}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={countdown > 0 || verificationState === 'loading'}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium min-w-[100px]"
                  >
                    {countdown > 0 ? `${countdown}s` : verificationState === 'loading' ? '发送中...' : '发送验证码'}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || registerState === 'loading' || registrationType === 'wechat'}
              className={`btn w-full ${registrationType === 'wechat' ? 'btn-secondary cursor-not-allowed' : 'btn-primary'}`}
            >
              {registrationType === 'wechat'
                ? '微信扫码注册（开发中）'
                : isLoading || registerState === 'loading'
                  ? '注册中...'
                  : '注册'
              }
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              已有账号？{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
                立即登录
              </Link>
            </p>
          </div>

          {/* 注册方式选择 */}
          <div className="mt-4 text-center">
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={() => setRegistrationType('phone')}
                className={`text-sm ${registrationType === 'phone'
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-500 hover:text-primary-600'
                  }`}
              >
                手机号注册
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() => setRegistrationType('email')}
                className={`text-sm ${registrationType === 'email'
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-500 hover:text-primary-600'
                  }`}
              >
                邮箱注册
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() => setRegistrationType('wechat')}
                className={`text-sm ${registrationType === 'wechat'
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-500 hover:text-primary-600'
                  }`}
              >
                微信扫码注册
              </button>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default RegisterPage;